import DialogRootStore from 'store/dialog.root.store';
import { action, observable, makeObservable } from 'mobx';
import { loadable } from '@fulcrumgt/mobx-store-utils';
import ImmutableTemplate from '../../api/immutables/ImmutableTemplate';
import { Client, Matter } from '../../api/types/types';
import ImmutableTimer from '../../api/immutables/ImmutableTimer';
import { debounce } from 'typescript-debounce-decorator';
import { Timer } from '../../api/implementations/electron/Dexie';
import { RootStore } from 'store/root.store';

export interface TimerFormValidation {
    name?: string | boolean;
}

export default class TimerNewDialogStore extends DialogRootStore<boolean, ImmutableTimer[]> {
    @observable name: string = '';
    @observable template?: ImmutableTemplate | null;
    @observable matter?: Matter | null;
    @observable client?: Client | null;
    @observable start: boolean = false;
    @observable validation: TimerFormValidation = { name: false};
    @observable saving: boolean = false;
    @observable isQuickTimer: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this);
    }

    @action.bound toggleStart() {
        this.start = !this.start;
    }
    @action.bound setName(name: string) {
        this.name = name;
        this.validation.name = false;
    }

    @action.bound setTemplate(template?: ImmutableTemplate) {
        this.template = template;
        if (!this.name && template) {
            this.name = template.name;
            this.validation.name = false;
        }
        this.client = null;
        this.matter = null;
    }
    @action.bound setClient(client?: Client) {
        this.client = client;
        this.matter = null;
    }
    @loadable()
    @action.bound
    async setMatter(matter?: Matter) {
        this.matter = matter;
        if (matter) {
            if (!matter.tracked) {
                await this.rootStore.api.Matter.track([matter.id]);
            }
            this.client = await this.rootStore.api.Client.get(matter.clientId);
            if (!this.name && this.client) {
                const clientName = this.client.name.substring(0, 40);
                this.setName(`${clientName} - ${matter.name}`);
                this.validation.name = false;
            }
        }
        this.template = null;
    }
    @action.bound
    async onOpen(isQuickTimer: boolean) {
        this.name = '';
        this.template = undefined;
        this.matter = undefined;
        this.client = undefined;
        this.start = false;
        this.saving = false;
        this.validation = Object.assign({ name: false });
        if (isQuickTimer) {
            this.setName(this.generateQuickTimerName());
            this.start = true;
        }
    }
    generateQuickTimerName = (): string => {
        const prefix = '$QuickTimer-';
        const existingNumbers = this.rootStore.timerStore.timers.reduce((acc, t) => {
            if (t.timeKeeperId === this.rootStore.appStore.currentTimekeeper.timeKeeperId && t.name.startsWith(prefix)) {
                const num = parseInt(t.name.replace(prefix, ''), 10);
                if (!isNaN(num)) {
                    acc.push(num);
                }
            }
            return acc;
        }, [] as number[]);
        let counter = 1;
        existingNumbers.sort((a, b) => a - b);
        for (const num of existingNumbers) {
            if (num !== counter) {
                break;
            }
            counter++;
        }
        return `${prefix}${String(counter).padStart(3, '0')}`;
    };
    validateTimerName = (timer: ImmutableTimer) => {
        let duplicates = this.rootStore.timerStore.timers.filter(t => (
            t.name.trim().toUpperCase() === timer.name.trim().toUpperCase()
        ));
        if (duplicates.length > 0) {
            this.validation = {
                ...this.validation,
                name: 'dialog.new_timer.validation.name.duplicate'
            };
        }
        if (timer.name.length > 100) {
            this.validation.name = 'dialog.new_timer.validation.name.length';
        }
        if (timer.name.trim() === '') {
            this.validation = Object.assign({ name: 'dialog.new_timer.validation.name.required'});
        }
        return !this.validation.name;
    }
    @loadable()
    @action.bound
    async saveNew() {
        let timer = Object.assign(new ImmutableTimer(), {
            name: this.name.trim().replace(/\s\s+/g, ' '),
            matterId: this.matter ? this.matter.id : undefined,
            templateId: this.template ? this.template.id : undefined,
            timeKeeperId: this.rootStore.appStore.currentTimekeeper.timeKeeperId!,
            deleted: false,
            favorite: false
        });
        if (!this.validateTimerName(timer)) {
            return [];
        }
        let createdTimers: ImmutableTimer[] = [];
        if (this.start) {
            let stoppedTimer = await this.rootStore.api.Timer.stop();
            if (stoppedTimer) {
                createdTimers.push(stoppedTimer);
            }
            let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            timer.active = true;
            timer.startedOn = (new Date(new Date().setMilliseconds(0))).toISOString();
            timer.startedTimezone = timeZone;
            timer.notes = '';
        } else {
            timer.active = false;
        }
        let resp = await this.rootStore.api.Timer.updateTimers([timer]);
        if (!resp.length) {
            throw new Error('Cannot create timer, Try again'); // TODO: Translation
        }
        const respTimer = resp[0].object as unknown as Timer;
        if (!respTimer.id && respTimer.localId) {
            respTimer.id = respTimer.localId * -1;
        }
        createdTimers.push(await this.rootStore.api.Timer.get(respTimer.id!));
        this.rootStore.snackbarStore.triggerSnackbar('app.snackbar.info.saved');
        return createdTimers;
    }
    @action.bound @debounce(500, {leading: false}) async doSave() {
        if (this.saving) {
            return;
        }
        this.saving = true;
        let createdTimers = await this.saveNew();
        if (createdTimers.length) {
            this.resolveAndClose(createdTimers);
        } else {
            this.saving = false;
            return;
        }
        this.saving = false;
    }
}